import React, { useState } from 'react';
import { Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useResponseFormatting } from './ResponseFormattingContext';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// Use only commonly available themes
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface JsonBlockProps {
  data: any;
  title?: string;
  collapsible?: boolean;
}

export const JsonBlock: React.FC<JsonBlockProps> = ({ 
  data, 
  title = "JSON Response", 
  collapsible = true 
}) => {
  const { settings } = useResponseFormatting();
  const [isExpanded, setIsExpanded] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);

  const jsonString = JSON.stringify(data, null, 2);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy JSON:', err);
    }
  };

  const getTheme = () => {
    // Use the default prism theme for consistency
    return prism;
  };

  const renderHighlightedJson = () => {
    if (!settings.syntaxHighlighting) {
      return (
        <pre className="text-sm overflow-x-auto p-4 bg-muted rounded-md border">
          <code className="text-foreground">{jsonString}</code>
        </pre>
      );
    }

    return (
      <SyntaxHighlighter
        language="json"
        style={getTheme()}
        customStyle={{
          margin: 0,
          background: 'transparent',
          padding: '1rem',
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
        }}
        wrapLongLines={true}
      >
        {jsonString}
      </SyntaxHighlighter>
    );
  };

  return (
    <div className="my-4 border border-border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted/50 border-b border-border">
        <div className="flex items-center space-x-2">
          {collapsible && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          )}
          <span className="text-sm font-medium text-foreground">{title}</span>
          <span className="text-xs text-muted-foreground">
            ({typeof data === 'object' ? Object.keys(data).length : 1} {Object.keys(data).length === 1 ? 'property' : 'properties'})
          </span>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={handleCopy}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="relative">
          {renderHighlightedJson()}
          
          {copySuccess && (
            <div className="absolute top-2 right-2 bg-primary text-primary-foreground px-2 py-1 rounded text-xs">
              Copied!
            </div>
          )}
        </div>
      )}
    </div>
  );
};